<template>
  <SfModal
    v-if="modalOpen"
    class="sf-modal-l"
    content-class-name="!tw-overflow-visible"
    footer-align="center"
    @close="close"
  >
    <template #title>
      {{
        I18n.t('company_consolidations.settings_modal.title', {
          company_name: companyName,
        })
      }}
    </template>
    <template #body>
      <div>
        <div v-if="errorMessage" class="sf-alert sf-alert-danger tw-mb-4">
          {{ errorMessage }}
        </div>
        <div class="tw-bg-gray-100 tw-p-4 tw-mb-6 tw-rounded-lg">
          <div class="tw-flex tw-items-center">
            <span class="tw-text-base tw-font-medium tw-mr-2">{{
              companyName
            }}</span>
            <span
              v-if="companyCurrency"
              :class="currencyClass"
              :title="currencyTooltipText"
            >
              {{ companyCurrency }}
            </span>
            <i
              v-if="shouldShowCurrencyMismatch"
              class="fa fa-triangle-exclamation tw-text-red-500 tw-ml-1"
              :title="currencyTooltipText"
            />
          </div>
          <div
            v-if="companyFileNumber"
            class="tw-text-sm tw-text-gray-500 tw-mt-2"
          >
            {{
              I18n.t(
                'company_consolidations.settings_modal.company_file_number',
                { file_number: companyFileNumber }
              )
            }}
          </div>
        </div>

        <div class="tw-mt-0 tw-mb-4">
          <h3 class="tw-text-sm tw-font-medium tw-mb-4">
            {{ I18n.t('company_consolidations.settings_modal.setup_options') }}
          </h3>

          <div v-if="!isSubconsolidation">
            <div class="tw-h-px tw-bg-gray-300 tw-w-full"></div>

            <div class="tw-py-3">
              <div
                class="
                  tw-flex
                  tw-items-center
                  tw-justify-between
                  tw-w-full
                  tw-min-h-[44px]
                "
              >
                <div class="tw-flex tw-items-center">
                  <SfCheckbox
                    v-model="isParentCompany"
                    :label="
                      I18n.t(
                        'company_consolidations.settings_modal.parent_company.label'
                      )
                    "
                  />
                  <i
                    v-tooltip="
                      I18n.t(
                        'company_consolidations.settings_modal.parent_company.tooltip'
                      )
                    "
                    class="fa fa-info-circle tw-ml-2 tw-text-gray-500"
                  />
                </div>
                <div
                  v-if="parentCompanyName"
                  :class="[
                    'tw-text-sm',
                    'tw-px-3',
                    'tw-py-1',
                    'tw-rounded-full',
                    isParentCompany
                      ? 'tw-text-green-700 tw-bg-green-100'
                      : 'tw-text-gray-600 tw-bg-gray-100',
                  ]"
                >
                  <span
                    v-dompurify-html="
                      isParentCompany
                        ? I18n.t(
                            'company_consolidations.settings_modal.marked_as_parent_company'
                          )
                        : I18n.t(
                            'company_consolidations.settings_modal.company_is_currently_marked_as_parent',
                            { company_name: parentCompanyName }
                          )
                    "
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="tw-h-px tw-bg-gray-300 tw-w-full"></div>

          <div data-testid="join-date" class="tw-py-3">
            <div
              class="
                tw-flex
                tw-items-center
                tw-justify-between
                tw-w-full
                tw-min-h-[44px]
              "
            >
              <div class="tw-flex tw-items-center">
                <SfCheckbox
                  v-model="hasJoinDate"
                  :label="
                    I18n.t(
                      'company_consolidations.settings_modal.join_date.label'
                    )
                  "
                />
                <i
                  v-tooltip="
                    I18n.t(
                      'company_consolidations.settings_modal.join_date.tooltip'
                    )
                  "
                  class="fa fa-info-circle tw-ml-2 tw-text-gray-500"
                />
              </div>
              <div class="tw-w-1/2 tw-relative tw-z-[100]">
                <SfDropdownSearchable
                  v-if="hasJoinDate"
                  v-model="startDate"
                  :options="joinDateOptions"
                  :placeholder="
                    I18n.t(
                      'company_consolidations.settings_modal.join_date.placeholder'
                    )
                  "
                  :search-placeholder="
                    I18n.t(
                      'company_consolidations.settings_modal.join_date.search_placeholder'
                    )
                  "
                  :is-full-width="true"
                  dropdown-id="join-date-dropdown"
                />
                <div v-else class="tw-h-9"></div>
              </div>
            </div>
          </div>

          <div class="tw-h-px tw-bg-gray-300 tw-w-full"></div>

          <div data-testid="exit-date" class="tw-py-3">
            <div
              class="
                tw-flex
                tw-items-center
                tw-justify-between
                tw-w-full
                tw-min-h-[44px]
              "
            >
              <div class="tw-flex tw-items-center">
                <SfCheckbox
                  v-model="hasExitDate"
                  :label="
                    I18n.t(
                      'company_consolidations.settings_modal.exit_date.label'
                    )
                  "
                />
                <i
                  v-tooltip="
                    I18n.t(
                      'company_consolidations.settings_modal.exit_date.tooltip'
                    )
                  "
                  class="fa fa-info-circle tw-ml-2 tw-text-gray-500"
                />
              </div>
              <div class="tw-w-1/2 tw-relative tw-z-[99]">
                <SfDropdownSearchable
                  v-if="hasExitDate"
                  v-model="endDate"
                  :options="exitDateOptions"
                  :placeholder="
                    I18n.t(
                      'company_consolidations.settings_modal.exit_date.placeholder'
                    )
                  "
                  :search-placeholder="
                    I18n.t(
                      'company_consolidations.settings_modal.exit_date.search_placeholder'
                    )
                  "
                  :is-full-width="true"
                  dropdown-id="exit-date-dropdown"
                />
                <div v-else class="tw-h-9"></div>
              </div>
            </div>
          </div>

          <div class="tw-h-px tw-bg-gray-300 tw-w-full"></div>
        </div>

        <div v-if="hasSubconsolidations" class="tw-mt-6 tw-mb-4">
          <h3 class="tw-text-sm tw-font-medium tw-mb-4">
            {{ I18n.t('company_consolidations.settings_modal.entities') }}
          </h3>
          <div class="tw-text-sm tw-text-gray-600 tw-mb-4">
            {{
              I18n.t(
                'company_consolidations.settings_modal.entities_description'
              )
            }}
            <a
              :href="consolidationSettingsPageUrl"
              class="sf-a sf-c-primary"
              target="_blank"
            >
              {{
                I18n.t(
                  'company_consolidations.settings_modal.consolidation_settings_page'
                )
              }} </a
            >.
          </div>
          <table class="sf-table sf-table-border sf-table-middle tw-w-full">
            <tbody>
              <tr v-for="entity in subconsolidations" :key="entity.id">
                <td class="tw-py-3 tw-min-h-[50px]">
                  <div class="tw-flex tw-items-center">
                    <a
                      :href="entity.link"
                      class="sf-a tw-group"
                      target="_blank"
                    >
                      {{ entity.name }}
                      <i
                        class="
                          fa-solid fa-external-link-alt fa-sm
                          tw-ml-2 tw-opacity-0
                          group-hover:tw-opacity-100
                          tw-transition-opacity
                        "
                      />
                    </a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="sf-level sf-level-center">
        <button
          class="sf-btn sf-btn-default tw-mr-4"
          :disabled="isSaving"
          @click="close"
        >
          {{ I18n.t('company_consolidations.settings_modal.buttons.cancel') }}
        </button>
        <button
          class="sf-btn sf-btn-primary"
          :disabled="isSaving"
          @click="saveAndClose"
        >
          <span
            v-if="isSaving"
            class="sf-spinner sf-spinner-primary sf-inline-block"
          />
          {{ I18n.t('company_consolidations.settings_modal.buttons.save') }}
        </button>
      </div>
    </template>
  </SfModal>
</template>

<script>
import { SfCheckbox } from '@silverfin/ui-components'
import SfDropdownSearchable from '@app/shared/vue/components/sf-dropdown/sf-dropdown-searchable.vue'
import { SfModal } from '@app/shared/vue/components/sf-modal'
import { updateConsolidation, fetchPeriodPickerData } from '../api/index'

export default {
  name: 'CompanyConsolidationSettingsModal',
  components: {
    SfCheckbox,
    SfDropdownSearchable,
    SfModal,
  },
  data() {
    return {
      modalOpen: false,
      companyName: '',
      companyCurrency: '',
      companyFileNumber: '',
      startDate: null,
      endDate: null,
      hasJoinDate: false,
      hasExitDate: false,
      periodPickerData: null,
      isLoadingPeriodData: false,
      isSaving: false,
      errorMessage: null,
      parentCompanyName: '',
      parentCompanyCurrency: '',
      consolidationCompanyCurrency: '',
      isParentCompany: false,
      initialIsParentCompany: false,
      subconsolidations: [],
      companyLink: '#',
    }
  },

  computed: {
    joinDateOptions() {
      return this.getJoinDateOptions()
    },
    exitDateOptions() {
      return this.getExitDateOptions()
    },
    currenciesMatch() {
      return this.companyCurrency === this.consolidationCompanyCurrency
    },
    shouldShowCurrencyMismatch() {
      return (
        this.consolidationCompanyCurrency &&
        this.companyCurrency &&
        !this.currenciesMatch
      )
    },
    currencyClass() {
      return this.shouldShowCurrencyMismatch
        ? 'sf-label sf-label-danger'
        : 'sf-label sf-label-primary'
    },
    currencyTooltipText() {
      if (!this.companyCurrency) {
        return I18n.t('company_consolidations.tooltip.no_subsidiary_currency')
      } else if (!this.consolidationCompanyCurrency) {
        return I18n.t('company_consolidations.tooltip.no_parent_currency')
      } else if (this.currenciesMatch) {
        return I18n.t('company_consolidations.tooltip.currency_matches')
      } else {
        return I18n.t('company_consolidations.tooltip.currency_mismatch')
      }
    },
    hasSubconsolidations() {
      return this.subconsolidations && this.subconsolidations.length > 0
    },
    consolidationSettingsPageUrl() {
      return this.companyLink
    },
  },

  methods: {
    async openModal({
      companyConsolidation,
      subsidiaryCompany,
      parentCompany,
      consolidationCompany,
      subconsolidations = [],
      companyLink = '#',
    }) {
      this._consolidationId = companyConsolidation.id
      this._companyId = subsidiaryCompany.id
      this._currentLedgerId = subsidiaryCompany.current_ledger_id
      this.companyName = subsidiaryCompany.name || ''
      this.companyCurrency = subsidiaryCompany.currency || ''
      this.companyFileNumber = subsidiaryCompany.file_number || ''
      this.parentCompanyName = parentCompany.name || ''
      this.parentCompanyCurrency = parentCompany.currency || ''
      this.consolidationCompanyCurrency = consolidationCompany?.currency || ''
      this.isParentCompany = parentCompany.id === subsidiaryCompany.id
      this.initialIsParentCompany = this.isParentCompany
      this.subconsolidations = subconsolidations || []
      this.companyLink = companyLink
      this.isSubconsolidation = subsidiaryCompany.subconsolidation

      this.startDate = companyConsolidation.start_date || null
      this.endDate = companyConsolidation.end_date || null

      this.hasJoinDate = !!companyConsolidation.start_date
      this.hasExitDate = !!companyConsolidation.end_date

      await this.fetchPeriodData()
      this.modalOpen = true
    },

    async fetchPeriodData() {
      if (!this._companyId || !this._currentLedgerId) return

      try {
        this.isLoadingPeriodData = true
        this.errorMessage = null
        this.periodPickerData = await fetchPeriodPickerData(
          this._companyId,
          this._currentLedgerId
        )
      } catch {
        this.errorMessage = I18n.t(
          'company_consolidations.settings_modal.errors.fetch_period_data'
        )
      } finally {
        this.isLoadingPeriodData = false
      }
    },

    formatPeriodLabel(period, year) {
      const isYearlyPeriod = /^\d{4}$/.test(String(period.label))

      if (isYearlyPeriod) {
        return String(period.label)
      }

      // Backend already sends translated month names
      return `${period.label} ${year.label}`
    },

    formatDateForDisplay(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const day = date.getDate()
      const month = date.getMonth() + 1
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },

    getPeriodStartDate(periodEndDate) {
      if (!periodEndDate || !this.periodPickerData) return periodEndDate

      const allPeriods = []
      this.periodPickerData.years.forEach((year) => {
        allPeriods.push(...year.periods)
      })

      const currentPeriodIndex = allPeriods.findIndex(
        (p) => p.periodEndDate === periodEndDate
      )

      if (currentPeriodIndex > 0) {
        const prevPeriodEndDate =
          allPeriods[currentPeriodIndex - 1].periodEndDate
        const [prevYear, prevMonth, prevDay] = prevPeriodEndDate
          .split('-')
          .map(Number)
        const prevEndDate = new Date(prevYear, prevMonth - 1, prevDay)
        const startDate = new Date(prevEndDate)
        startDate.setDate(startDate.getDate() + 1)

        const year = startDate.getFullYear()
        const month = (startDate.getMonth() + 1).toString().padStart(2, '0')
        const day = startDate.getDate().toString().padStart(2, '0')
        return `${year}-${month}-${day}`
      } else {
        const year = this.periodPickerData.years.find((y) =>
          y.periods.some((p) => p.periodEndDate === periodEndDate)
        )

        if (year) {
          const yearStartDate = new Date(year.startDate)
          const yearVal = yearStartDate.getFullYear()
          const month = (yearStartDate.getMonth() + 1)
            .toString()
            .padStart(2, '0')
          const day = yearStartDate.getDate().toString().padStart(2, '0')
          return `${yearVal}-${month}-${day}`
        }
      }

      return periodEndDate
    },

    getJoinDateOptions() {
      if (!this.periodPickerData || !this.periodPickerData.years) {
        return []
      }

      const options = []
      const selectedEndDate = this.endDate

      this.periodPickerData.years.forEach((year) => {
        year.periods.forEach((period) => {
          if (period.periodEndDate && period.ledgerId) {
            if (selectedEndDate && period.periodEndDate > selectedEndDate) {
              return
            }

            const startDateStr = this.getPeriodStartDate(period.periodEndDate)
            const label = this.formatPeriodLabel(period, year)
            const formattedStartDate = this.formatDateForDisplay(startDateStr)

            const periodInfo = I18n.t(
              'company_consolidations.settings_modal.join_date.period_starting_at',
              { date: formattedStartDate }
            )

            options.push({
              label: `${label} ${periodInfo}`,
              value: startDateStr,
            })
          }
        })
      })

      return options.reverse()
    },

    getExitDateOptions() {
      if (!this.periodPickerData || !this.periodPickerData.years) {
        return []
      }

      const options = []
      const selectedStartDate = this.startDate

      this.periodPickerData.years.forEach((year) => {
        year.periods.forEach((period) => {
          if (period.periodEndDate && period.ledgerId) {
            if (selectedStartDate) {
              const periodStartDate = this.getPeriodStartDate(
                period.periodEndDate
              )
              if (periodStartDate < selectedStartDate) {
                return
              }
            }

            const label = this.formatPeriodLabel(period, year)
            const formattedEndDate = this.formatDateForDisplay(
              period.periodEndDate
            )

            const periodInfo = I18n.t(
              'company_consolidations.settings_modal.exit_date.period_ending_at',
              { date: formattedEndDate }
            )

            options.push({
              label: `${label} ${periodInfo}`,
              value: period.periodEndDate,
            })
          }
        })
      })

      return options.reverse()
    },
    close() {
      this.modalOpen = false
    },
    async saveAndClose() {
      if (this.isSaving) {
        return
      }

      try {
        this.isSaving = true
        this.errorMessage = null

        const payload = {
          startDate: this.hasJoinDate ? this.startDate : null,
          endDate: this.hasExitDate ? this.endDate : null,
        }

        // Only send setParentCompany if the value has changed
        if (this.isParentCompany !== this.initialIsParentCompany) {
          payload.setParentCompany = this.isParentCompany
        }

        await updateConsolidation(this._consolidationId, payload)
        this.close()
        window.location.reload()
      } catch {
        this.errorMessage = I18n.t(
          'company_consolidations.settings_modal.errors.save_failed'
        )
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>

<style scoped>
/* Dropdown menu positioning and height */
:deep(.sf-dropdown2-menu) {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 10000;
}

:deep(.sf-dropdown2-menu .tw-overflow-y-auto) {
  max-height: 200px;
}
</style>
